<template>
  <div ref="chartContainer"
    class="flex items-center justify-center w-full h-full">
    <!-- CumulatedSalesChart.vue -->
    <div ref="chart" class="w-full min-w-full h-72"></div>
  </div>
</template>
<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { useAppSettingsStore } from '../../stores/AppSettingsStore';
import { formatCurrencyForCenter, formatAmount } from '../../utils/Languages';
import { useIsDarkMode } from '../../composables/useIsDarkMode';
import * as echarts from 'echarts';

export type SalesChartType = 'line' | 'bar'

const props = defineProps<{
  currentData: (number | null)[]
  previousData: number[]
  chartType: SalesChartType
  chartId: string
  centerId: number
  boundaries: string[]
  xAxis: string[]
  projectionData?: (number | null)[]
  cutoffIndex?: number
}>();

const chartContainer = ref<HTMLElement>();
const chart = ref<HTMLElement>();
const myChart = shallowRef<echarts.ECharts>();
const appSettingsStore = useAppSettingsStore();

const isDark = useIsDarkMode();

const getGradient = (colorStart: string, colorEnd: string) => ({
  type: 'linear',
  x: 0, y: 1, x2: 0, y2: 0,
  colorStops: [
    { offset: 0, color: colorStart },
    { offset: 1, color: colorEnd }
  ],
  global: false
});

// Apex Timing palette — current: #06C8FF cyan, previous: brand neutrals (#282727 / #F4F4F4)
const getPalette = (dark: boolean) => ({
  current: {
    bar: dark ? 'rgba(6, 200, 255, 0.80)' : 'rgba(6, 200, 255, 0.85)',
    line: '#06C8FF',
    areaStart: 'rgba(6, 200, 255, 0.30)',
    areaEnd: 'rgba(6, 200, 255, 0.02)',
    shadow: 'rgba(6, 200, 255, 0.25)',
  },
  previous: {
    bar: dark ? 'rgba(244, 244, 244, 0.30)' : 'rgba(40, 39, 39, 0.20)',
    line: dark ? 'rgba(244, 244, 244, 0.50)' : 'rgba(40, 39, 39, 0.35)',
    areaStart: dark ? 'rgba(244, 244, 244, 0.12)' : 'rgba(40, 39, 39, 0.08)',
    areaEnd: 'rgba(0, 0, 0, 0.01)',
    shadow: dark ? 'rgba(244, 244, 244, 0.08)' : 'rgba(40, 39, 39, 0.08)',
  }
});

const getOptions = async () => {
  const dark = isDark.value;
  const palette = getPalette(dark);
  const hasProjection = props.projectionData && props.chartType === 'line';
  // Pad currentData with zeros if it's empty
  const paddingLength = props.xAxis.length > 0 ? props.xAxis.length : props.previousData.length;
  const paddedCurrentData = props.currentData.length === 0 && paddingLength > 0
    ? new Array(paddingLength).fill(0)
    : props.currentData;

  const center = appSettingsStore.getCenter(props.centerId);

  let options = {
    animation: true,
    animationDuration: 600,
    animationEasing: 'cubicOut' as const,
    animationDelay: (idx: number) => idx * 40,
    grid: {
      left: '1%',
      right: hasProjection ? '12%' : '3%',
      bottom: '12%',
      top: props.previousData.length > 0 ? '14%' : '8%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: props.xAxis,
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: dark ? 'rgba(244, 244, 244, 0.75)' : 'rgba(40, 39, 39, 0.6)',
        fontSize: 10,
        fontWeight: 600,
        fontFamily: 'Lato, system-ui, sans-serif',
        margin: 10
      }
    },
    legend: props.previousData.length > 0 ? {
      data: props.boundaries,
      top: 0,
      left: 'center',
      textStyle: {
        color: dark ? 'rgba(244, 244, 244, 0.80)' : 'rgba(40, 39, 39, 0.7)',
        fontSize: 10,
        fontWeight: 600,
        fontFamily: 'Lato, system-ui, sans-serif'
      },
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 16,
      icon: 'circle'
    } : { show: false },
    textStyle: {
      fontFamily: 'Lato, system-ui, sans-serif'
    },
    dataZoom: [
      {
        id: `${props.chartId}-dataZoom`,
        type: 'inside',
        xAxisIndex: [0],
      },
    ],
    tooltip: {
      trigger: 'axis',
      backgroundColor: dark ? 'rgba(40, 39, 39, 0.95)' : 'rgba(255, 255, 255, 0.98)',
      borderColor: dark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(226, 232, 240, 0.8)',
      borderWidth: 1,
      borderRadius: 10,
      padding: [10, 14],
      textStyle: {
        color: dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(15, 23, 42, 0.9)',
        fontSize: 12,
        fontFamily: 'Lato, system-ui, sans-serif'
      },
      extraCssText: dark
        ? 'box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);'
        : 'box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);',
      confine: true,
      position: function (point: number[], _params: any, _dom: HTMLElement, _rect: any, size: any) {
        const [x, y] = point;
        const { contentSize, viewSize } = size;

        let posX = x + 10;
        let posY = y - contentSize[1] / 2;

        if (posX + contentSize[0] > viewSize[0]) posX = x - contentSize[0] - 10;
        if (posX < 0) posX = 10;
        if (posY < 0) posY = 10;
        if (posY + contentSize[1] > viewSize[1]) posY = viewSize[1] - contentSize[1] - 10;

        return [posX, posY];
      },
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: dark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(6, 200, 255, 0.04)'
        }
      },
      formatter: (params: any) => {
        const all = (Array.isArray(params) ? params : [params])
          .filter((p: any) => p.value != null);
        if (all.length === 0) return '';
        // Past/present: show current + previous, hide projection
        // Future: show projection + previous, hide current
        const hasCurrent = all.some((p: any) => p.seriesName === props.boundaries[1] && p.value != null);
        const items = hasCurrent
          ? all.filter((p: any) => p.seriesName !== 'Projection')
          : all.filter((p: any) => p.seriesName !== props.boundaries[1]);
        if (items.length === 0) return '';
        let html = `<div style="font-weight:600;margin-bottom:4px">${items[0].axisValueLabel}</div>`;
        for (const p of items) {
          html += `<div style="display:flex;align-items:center;gap:6px;margin:2px 0">`;
          html += `${p.marker}<span>${p.seriesName}</span>`;
          html += `<span style="font-weight:600;margin-left:auto">${formatCurrencyForCenter(p.value, center)}</span>`;
          html += `</div>`;
        }
        return html;
      },
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitNumber: 4,
      splitLine: {
        lineStyle: {
          color: dark ? 'rgba(244, 244, 244, 0.12)' : 'rgba(226, 232, 240, 0.5)',
          type: [4, 4] as [number, number]
        }
      },
      axisLabel: {
        color: dark ? 'rgba(244, 244, 244, 0.70)' : 'rgba(40, 39, 39, 0.55)',
        fontSize: 10,
        fontWeight: 500,
        fontFamily: 'Lato, system-ui, sans-serif',
        formatter: (value: number) => {
          if (!center) return value.toString();
          if (value === 0) return `0 ${center.currencySymbol || '€'}`;
          return formatAmount(value, { center, compact: true });
        }
      }
    },
    series: [
      {
        type: props.chartType,
        name: props.boundaries[1],
        barGap: '15%',
        barMaxWidth: 28,
        data: paddedCurrentData,
        itemStyle: {
          color: props.chartType === 'bar' ? palette.current.bar : palette.current.line,
          borderRadius: props.chartType === 'bar' ? [4, 4, 1, 1] : undefined,
        },
        emphasis: {
          itemStyle: {
            shadowColor: palette.current.shadow,
            shadowBlur: 10,
            shadowOffsetY: 2
          }
        },
        lineStyle: {
          color: palette.current.line,
          width: 2.5,
        },
        areaStyle: props.chartType === 'line' ? {
          color: getGradient(palette.current.areaStart, palette.current.areaEnd),
        } : undefined,
        smooth: props.chartType === 'line' ? 0.35 : undefined,
        symbolSize: props.chartType === 'line' ? 4 : undefined,
        showSymbol: props.chartType === 'line' ? false : undefined,
      }
    ]
  }

  if (props.previousData.length > 0) {
    let originalSeries = options.series

    let previousSerie = {
      type: props.chartType,
      name: props.boundaries[0],
      barGap: '15%',
      barMaxWidth: 28,
      data: props.previousData,
      itemStyle: {
        color: palette.previous.bar,
        borderRadius: props.chartType === 'bar' ? [4, 4, 1, 1] : undefined,
      },
      emphasis: {
        itemStyle: {
          shadowColor: palette.previous.shadow,
          shadowBlur: 8,
          shadowOffsetY: 1
        }
      },
      lineStyle: {
        color: palette.previous.line,
        width: 1.5,
        type: 'dashed',
      },
      areaStyle: props.chartType === 'line' ? {
        color: getGradient(palette.previous.areaStart, palette.previous.areaEnd),
      } : undefined,
      smooth: props.chartType === 'line' ? 0.35 : undefined,
      symbolSize: props.chartType === 'line' ? 3 : undefined,
      showSymbol: props.chartType === 'line' ? false : undefined,
    }
    options.series = [
      previousSerie,
      ...originalSeries
    ]
  }

  // --- Projection series (dashed cyan line) ---
  if (hasProjection && props.projectionData) {
    const projSeries: any = {
      type: 'line',
      name: 'Projection',
      data: props.projectionData,
      smooth: 0.35,
      lineStyle: { width: 2, color: palette.current.line, type: [4, 4], opacity: 0.4 },
      itemStyle: { color: palette.current.line, opacity: 0 },
      symbol: 'none',
      showSymbol: false,
      connectNulls: false,
      z: 8,
      areaStyle: undefined,
    };

    // End-of-period markPoint showing projected value
    const lastProjected = props.projectionData[props.projectionData.length - 1];
    if (lastProjected != null) {
      projSeries.markPoint = {
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: { color: palette.current.line, opacity: 0.6 },
        label: {
          show: true,
          position: 'right',
          fontSize: 10,
          fontWeight: 'bold',
          color: dark ? 'rgba(6, 200, 255, 0.8)' : 'rgba(6, 180, 230, 0.9)',
          formatter: () => '~' + formatCurrencyForCenter(lastProjected, center),
        },
        data: [{ coord: [props.projectionData.length - 1, lastProjected] }],
      };
    }

    options.series.push(projSeries);

    // "Today" mark line at cutoff
    if (props.cutoffIndex != null && props.cutoffIndex > 0) {
      (options.series[options.series.length - 2] as any).markLine = {
        silent: true,
        symbol: 'none',
        animation: false,
        data: [{
          xAxis: props.cutoffIndex - 1,
          lineStyle: {
            color: dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
            type: 'solid',
            width: 1,
          },
          label: { show: false },
          symbol: 'none',
        }],
      };
    }
  }

  return options;
}

let refreshInterval = ref<NodeJS.Timeout>()


const initializeChart = async () => {
  chartContainer.value?.clientWidth;
  // get the width of chartContainer
  if (chart.value) {
    setTimeout(async () => {
      myChart.value = echarts.init(chart.value)
      await loadOptions()
    }, 1000)
  }
}

const loadOptions = async () => {
  let options = await getOptions()
  myChart.value?.setOption(options, true);
}

watch([() => props.currentData, () => props.previousData, () => props.chartType, () => props.xAxis, () => props.projectionData], async () => {
  if (myChart.value) {
    await loadOptions()
  }
}, { deep: true })

watch(isDark, () => {
  if (myChart.value) loadOptions()
})

onMounted(async () => {
  await initializeChart()
})

onBeforeUnmount(() => {
  clearInterval(refreshInterval.value)
  if (myChart.value) {
    myChart.value.dispose()
  }
})
</script>